import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and freeze detection
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Detect tenant freeze/expired 403 from middleware
    if (error.response?.status === 403 && error.response?.data?.frozen === true) {
      // Dispatch a custom event so App.js can switch to freeze screen
      window.dispatchEvent(new CustomEvent('tenant-frozen', {
        detail: {
          status: error.response.data.tenant_status,
          display_name: error.response.data.display_name,
        }
      }));
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const auth = {
  login: (username: string, password: string) =>
    api.post('/api/auth/login', { username, password }),
  getMe: () => api.get('/api/auth/me'),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
  forceChangePassword: (newPassword: string) =>
    api.post('/api/auth/force-change-password', null, { params: { new_password: newPassword } }),
  generateResetLink: (userId: string) =>
    api.post(`/api/auth/generate-reset-link/${userId}`),
};

// Branches
export const branches = {
  list: () => api.get('/api/branches'),
  get: (id: string) => api.get(`/api/branches/${id}`),
  create: (data: any) => api.post('/api/branches', data),
  toggleActive: (id: string) => api.post(`/api/branches/${id}/toggle-active`),
  toggleMemberPortal: (id: string) => api.post(`/api/branches/${id}/toggle-member-portal`),
};

// Users
export const users = {
  list: () => api.get('/api/users'),
  create: (data: any) => api.post('/api/users', data),
};

// Members
export const members = {
  list: (params?: any) => api.get('/api/members', { params }),
  get: (id: string) => api.get(`/api/members/${id}`),
  getOperationalSummary: (id: string) => api.get(`/api/members/${id}/operational-summary`),
  create: (data: any) => api.post('/api/members', data),
  search: (q: string) => api.get(`/api/members/search?q=${encodeURIComponent(q)}`),
  updateNotes: (id: string, notes: string) => api.patch(`/api/members/${id}/notes?notes=${encodeURIComponent(notes)}`),
  exportExcel: () => api.get('/api/members/export/excel', { responseType: 'blob' }),
  update: (id: string, data: { name?: string; phone?: string; email?: string; notes?: string }) => api.put(`/api/members/${id}`, data),
  delete: (id: string) => api.delete(`/api/members/${id}`),
  advancedFilter: (body: any) => api.post('/api/members/advanced-filter', body),
};

// Services (used by advanced filter to choose service IDs)
export const services = {
  list: (branchId?: string) => api.get('/api/services', { params: branchId ? { branch_id: branchId } : {} }),
};

// Packages
export const packages = {
  list: () => api.get('/api/packages'),
  create: (data: any) => api.post('/api/packages', data),
};

// Sales
export const sales = {
  delete: (id: string) => api.delete(`/api/sales/${id}`),
  list: (params?: any) => api.get('/api/sales', { params }),
  get: (id: string) => api.get(`/api/sales/${id}`),
  create: (data: any) => api.post('/api/sales', data),
};

// Payments
export const payments = {
  delete: (id: string) => api.delete(`/api/payments/${id}`),
  list: (params?: any) => api.get('/api/payments', { params }),
  create: (data: any) => api.post('/api/payments', data),
};

// Unpaid Members
export const unpaid = {
  list: () => api.get('/api/unpaid-members'),
};

// Dashboard
export const dashboard = {
  getMetrics: (month?: number, year?: number) =>
    api.get('/api/dashboard/metrics', { params: { month, year } }),
};

// Expenses
export const expenses = {
  list: (params?: any) => api.get('/api/expenses', { params }),
  create: (data: any) => api.post('/api/expenses', data),
  update: (id: string, data: any) => api.put(`/api/expenses/${id}`, null, { params: data }),
  delete: (id: string) => api.delete(`/api/expenses/${id}`),
};

// Expense Templates
export const expenseTemplates = {
  list: () => api.get('/api/expense-templates'),
  create: (data: any) => api.post('/api/expense-templates', data),
  update: (id: string, data: any) => api.put(`/api/expense-templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/expense-templates/${id}`),
  syncMonth: (month: number, year: number) =>
    api.post(`/api/expense-templates/sync-month?month=${month}&year=${year}`),
};

// Calendar
export const calendar = {
  getOperations: (month: number, year: number) =>
    api.get(`/api/calendar/operations?month=${month}&year=${year}`),
  getDayTimeline: (date: string) =>
    api.get(`/api/calendar/day-timeline?date=${date}`),
};

// Snapshots
export const snapshots = {
  list: () => api.get('/api/snapshots'),
  generate: (month: number, year: number) =>
    api.post(`/api/snapshots/generate?month=${month}&year=${year}`),
};

// Reports
export const reports = {
  exportExcel: (month: number, year: number) =>
    api.get(`/api/reports/export/excel?month=${month}&year=${year}`, { responseType: 'blob' }),
};

// Sales Export
export const salesExport = {
  exportExcel: (month?: number, year?: number) =>
    api.get(`/api/exports/sales${month && year ? `?month=${month}&year=${year}` : ''}`, { responseType: 'blob' }),
};

// Notifications
export const notifications = {
  list: (unreadOnly?: boolean) =>
    api.get('/api/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id: string) => api.post(`/api/notifications/${id}/read`),
};

// Audit Log
export const auditLog = {
  list: (params?: any) => api.get('/api/audit-log', { params }),
};

// Global Search
export const search = {
  global: (q: string) => api.get('/api/search', { params: { q } }),
};

// GM (General Manager) specific endpoints
export const gm = {
  getBranchMetrics: (branchId: string, month?: number, year?: number) =>
    api.get(`/api/gm/branch/${branchId}/metrics`, { params: { month, year } }),
  getBranchUnpaid: (branchId: string) =>
    api.get(`/api/gm/branch/${branchId}/unpaid`),
  getBranchSales: (branchId: string, params?: any) =>
    api.get(`/api/gm/branch/${branchId}/sales`, { params }),
  getBranchPayments: (branchId: string, params?: any) =>
    api.get(`/api/gm/branch/${branchId}/payments`, { params }),
  getBranchExpenses: (branchId: string, params?: any) =>
    api.get(`/api/gm/branch/${branchId}/expenses`, { params }),
  getBranchPackages: (branchId: string) =>
    api.get(`/api/gm/branch/${branchId}/packages`),
  getBranchMembers: (branchId: string, params?: any) =>
    api.get(`/api/gm/branch/${branchId}/members`, { params }),
  getBranchUsers: (branchId: string) =>
    api.get(`/api/gm/branch/${branchId}/users`),
  getBranchCalendarOperations: (branchId: string, month: number, year: number) =>
    api.get(`/api/gm/branch/${branchId}/calendar/operations`, { params: { month, year } }),
  getBranchDayTimeline: (branchId: string, date: string) =>
    api.get(`/api/gm/branch/${branchId}/calendar/day-timeline`, { params: { date } }),
  exportBranchMembers: (branchId: string) =>
    api.get(`/api/gm/branch/${branchId}/exports/members`, { responseType: 'blob' }),
  exportBranchSales: (branchId: string, month?: number, year?: number) =>
    api.get(`/api/gm/branch/${branchId}/exports/sales`, { params: { month, year }, responseType: 'blob' }),
  exportBranchReport: (branchId: string, month: number, year: number) =>
    api.get(`/api/gm/branch/${branchId}/exports/report`, { params: { month, year }, responseType: 'blob' }),
};

// Users extended
export const usersExtended = {
  list: () => api.get('/api/users'),
  create: (data: any) => api.post('/api/users', data),
  updatePermissions: (userId: string, permissions: any) =>
    api.put(`/api/users/${userId}/permissions`, permissions),
  freeze: (userId: string) => api.post(`/api/users/${userId}/freeze`),
  unfreeze: (userId: string) => api.post(`/api/users/${userId}/unfreeze`),
};

// Packages extended
export const packagesExtended = {
  list: () => api.get('/api/packages'),
  create: (data: any) => api.post('/api/packages', data),
  update: (packageId: string, data: any) =>
    api.put(`/api/packages/${packageId}`, null, { params: data }),
};

// Payments extended
export const paymentsExtended = {
  list: (params?: any) => api.get('/api/payments', { params }),
  create: (data: any) => api.post('/api/payments', data),
  update: (paymentId: string, data: any) =>
    api.put(`/api/payments/${paymentId}`, null, { params: data }),
};

// Expenses extended
export const expensesExtended = {
  list: (params?: any) => api.get('/api/expenses', { params }),
  create: (data: any) => api.post('/api/expenses', data),
  update: (expenseId: string, data: any) =>
    api.put(`/api/expenses/${expenseId}`, null, { params: data }),
};

// Password Reset
export const passwordReset = {
  generateLink: (userId: string) => api.post(`/api/auth/generate-reset-link/${userId}`),
  verifyToken: (token: string) => api.get(`/api/auth/verify-reset-token/${token}`),
  resetWithToken: (token: string, newPassword: string) => 
    api.post('/api/auth/reset-password-with-token', null, { params: { token, new_password: newPassword } }),
};

// Member Password Reset
export const memberPasswordReset = {
  requestReset: (memberId: string) => api.post('/api/member-auth/request-reset', { member_id: memberId }),
  confirmReset: (phone: string, resetCode: string, newPassword: string) =>
    api.post('/api/member-auth/confirm-reset', { phone, reset_code: resetCode, new_password: newPassword }),
};

// Employee Freeze/Unfreeze
export const employeeActions = {
  freeze: (userId: string) => api.post(`/api/users/${userId}/freeze`),
  unfreeze: (userId: string) => api.post(`/api/users/${userId}/unfreeze`),
};

// Lesson Calendar Management (for staff operational views)
export const lessonCalendar = {
  getWeeklyLessons: (date: string, branch_id?: string) => api.get('/api/calendar/lessons/weekly', { params: { date, ...(branch_id ? { branch_id } : {}) } }),
  getDailyLessons: (date: string, branch_id?: string) => api.get('/api/calendar/lessons/daily', { params: { date, ...(branch_id ? { branch_id } : {}) } }),
  getLessonDetail: (lessonId: string) => api.get(`/api/calendar/lessons/${lessonId}/detail`),
};

// Booking Activity Archive
export const bookingArchive = {
  list: (params?: any) => api.get('/api/booking-archive', { params }),
  getFilters: () => api.get('/api/booking-archive/filters'),
};

// Employee Performance
export const employeePerformance = {
  getEmployees: (params?: any) => api.get('/api/employee-performance/employees', { params }),
  getBranches: () => api.get('/api/employee-performance/branches'),
  getReport: (employeeId: string, month: number, year: number) =>
    api.get(`/api/employee-performance/${employeeId}`, { params: { month, year } }),
  updateBonusSettings: (employeeId: string, data: any) =>
    api.put(`/api/employee-performance/${employeeId}/bonus-settings`, data),
  getEvaluations: (employeeId: string, month?: number, year?: number) =>
    api.get(`/api/employee-performance/${employeeId}/evaluations`, { params: { month, year } }),
  createEvaluation: (employeeId: string, data: any) =>
    api.post(`/api/employee-performance/${employeeId}/evaluations`, data),
  deleteEvaluation: (evalId: string) =>
    api.delete(`/api/employee-performance/evaluations/${evalId}`),
  getServiceBonusRates: (employeeId: string) =>
    api.get(`/api/employee-performance/${employeeId}/service-bonus-rates`),
  updateServiceBonusRate: (employeeId: string, data: any) =>
    api.put(`/api/employee-performance/${employeeId}/service-bonus-rates`, data),
  deleteServiceBonusRate: (employeeId: string, serviceId: string) =>
    api.delete(`/api/employee-performance/${employeeId}/service-bonus-rates/${serviceId}`),
};

// Monthly Reports
export const monthlyReports = {
  get: (month: number, year: number, branchId?: string) =>
    api.get('/api/monthly-reports', { params: { month, year, branch_id: branchId } }),
  getBranches: () => api.get('/api/monthly-reports/branches'),
};
